import type { Site, Page, Component, PageLayout } from '../schemas/validation';

export type NavigationItem = { title: string; slug: string; orderPosition: number };

export interface ParsedSiteResponse {
  website: Site | null;
  pages: Page[];
  homePage: Page | null;
  homeComponents: Component[];
  navigation: NavigationItem[];
  theme: unknown;
  layout: PageLayout | null;
  showBranding: boolean;
}

export interface ParsedPageResponse {
  website: Site | null;
  page: Page | null;
  components: Component[];
  navigation: NavigationItem[];
  theme: unknown;
  layout: PageLayout | null;
  showBranding: boolean;
}

function parseComponentsField(raw: unknown): Component[] {
  if (!raw) return [];
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    // Handle {components: [...]} wrapper or bare array
    const arr = (parsed as { components?: unknown })?.components ?? parsed;
    return Array.isArray(arr) ? (arr as Component[]) : [];
  } catch (error) {
    console.error('Error parsing components:', error);
    return [];
  }
}

function parseLayout(rawLayout: unknown): PageLayout | null {
  if (!rawLayout) return null;
  const layout = rawLayout as PageLayout;
  if (layout.components && typeof layout.components === 'string') {
    try {
      const parsed = JSON.parse(layout.components as unknown as string);
      const arr = (parsed as { components?: unknown })?.components ?? parsed;
      return { ...layout, components: Array.isArray(arr) ? arr : [] } as PageLayout;
    } catch (error) {
      console.error('Error parsing layout components:', error);
    }
  }
  return layout;
}

export function parseSiteResponse(data: {
  site?: Site;
  pages?: Page[];
  navigation?: NavigationItem[];
  theme?: unknown;
  layout?: unknown;
  showBranding?: boolean;
}): ParsedSiteResponse {
  const pages = Array.isArray(data?.pages) ? data!.pages! : [];
  const homePage =
    pages.find(p => p.isHomePage || p.slug === '' || p.slug === 'home') || pages[0] || null;
  return {
    website: data?.site ?? null,
    pages,
    homePage,
    homeComponents: parseComponentsField(homePage?.components),
    navigation: data?.navigation ?? [],
    theme: data?.theme ?? null,
    layout: parseLayout(data?.layout),
    showBranding: data?.showBranding ?? false,
  };
}

export function parsePageResponse(data: {
  site?: Site;
  page?: Page;
  navigation?: NavigationItem[];
  theme?: unknown;
  layout?: unknown;
  showBranding?: boolean;
}): ParsedPageResponse {
  return {
    website: data?.site ?? null,
    page: data?.page ?? null,
    components: parseComponentsField(data?.page?.components),
    navigation: data?.navigation ?? [],
    theme: data?.theme ?? null,
    layout: parseLayout(data?.layout),
    showBranding: data?.showBranding ?? false,
  };
}
